/**
 * Performance utility functions for optimizing React components
 * Following DRY principles and performance best practices
 */

/**
 * Debounce function to limit the rate of function calls
 * Useful for search inputs, API calls, and expensive operations
 * 
 * @param {Function} func - The function to debounce
 * @param {number} wait - The delay in milliseconds
 * @param {boolean} immediate - Whether to execute immediately on first call
 * @returns {Function} - The debounced function
 * 
 * @example
 * const debouncedSearch = debounce((query) => {
 *   performSearch(query);
 * }, 300);
 */
export const debounce = (func, wait, immediate = false) => {
  let timeout;
  
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    
    if (callNow) func(...args);
  };
};

/**
 * Throttle function to limit function calls to once per specified time period
 * Useful for scroll events, resize events, and high-frequency operations
 * 
 * @param {Function} func - The function to throttle
 * @param {number} limit - The time limit in milliseconds
 * @returns {Function} - The throttled function
 * 
 * @example
 * const throttledScroll = throttle((event) => {
 *   handleScroll(event);
 * }, 100);
 */
export const throttle = (func, limit) => {
  let inThrottle;
  
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Memoization function to cache expensive function results
 * Useful for expensive calculations and API responses
 * 
 * @param {Function} func - The function to memoize
 * @param {Function} keyGenerator - Optional function to generate cache keys
 * @returns {Function} - The memoized function
 * 
 * @example
 * const memoizedCalculation = memoize((a, b) => {
 *   return expensiveCalculation(a, b);
 * });
 */
export const memoize = (func, keyGenerator) => {
  const cache = new Map();
  
  return function memoizedFunction(...args) {
    const key = keyGenerator ? keyGenerator(...args) : JSON.stringify(args);
    
    if (cache.has(key)) {
      return cache.get(key);
    }
    
    const result = func.apply(this, args);
    cache.set(key, result);
    
    return result;
  };
};

/**
 * Creates a performance monitoring wrapper for functions
 * Useful for tracking function execution times in development
 * 
 * @param {Function} func - The function to monitor
 * @param {string} name - The name for logging
 * @returns {Function} - The wrapped function
 * 
 * @example
 * const monitoredFunction = performanceMonitor(
 *   expensiveFunction,
 *   'ExpensiveFunction'
 * );
 */
export const performanceMonitor = (func, name) => {
  return function monitoredFunction(...args) {
    const startTime = performance.now();
    const result = func.apply(this, args);
    const endTime = performance.now();
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`${name} execution time: ${endTime - startTime}ms`);
    }
    
    return result;
  };
};

/**
 * Batch function calls to reduce the number of operations
 * Useful for DOM updates, API calls, and state updates
 * 
 * @param {Function} func - The function to batch
 * @param {number} delay - The batching delay in milliseconds
 * @returns {Function} - The batched function
 * 
 * @example
 * const batchedUpdate = batch((updates) => {
 *   applyUpdates(updates);
 * }, 50);
 */
export const batch = (func, delay = 16) => {
  let batch = [];
  let timeout;
  
  return function batchedFunction(item) {
    batch.push(item);
    
    if (timeout) {
      clearTimeout(timeout);
    }
    
    timeout = setTimeout(() => {
      func(batch);
      batch = [];
      timeout = null;
    }, delay);
  };
};

/**
 * Creates a cache with TTL (Time To Live) for temporary data storage
 * Useful for caching API responses and computed values
 * 
 * @param {number} ttl - Time to live in milliseconds
 * @returns {Object} - Cache object with get, set, and clear methods
 * 
 * @example
 * const cache = createTTLCache(5000); // 5 second TTL
 * cache.set('key', 'value');
 * const value = cache.get('key');
 */
export const createTTLCache = (ttl = 300000) => { // Default 5 minutes
  const cache = new Map();
  
  return {
    get(key) {
      const item = cache.get(key);
      if (!item) return null;
      
      if (Date.now() > item.expiry) {
        cache.delete(key);
        return null;
      }
      
      return item.value;
    },
    
    set(key, value) {
      const expiry = Date.now() + ttl;
      cache.set(key, { value, expiry });
    },
    
    clear() {
      cache.clear();
    },
    
    size() {
      return cache.size;
    }
  };
};

/**
 * Utility to measure and log component render performance
 * Useful for identifying performance bottlenecks in React components
 * 
 * @param {string} componentName - Name of the component
 * @returns {Object} - Object with start and end methods
 * 
 * @example
 * const renderTimer = measureRenderTime('MyComponent');
 * renderTimer.start();
 * // ... render logic
 * renderTimer.end();
 */
export const measureRenderTime = (componentName) => {
  let startTime;
  
  return {
    start() {
      startTime = performance.now();
    },
    
    end() {
      if (startTime && process.env.NODE_ENV === 'development') {
        const endTime = performance.now();
        console.log(`${componentName} render time: ${endTime - startTime}ms`);
      }
    }
  };
};

/**
 * Optimized array filtering with early termination
 * Useful for large datasets where you only need a subset of results
 * 
 * @param {Array} array - The array to filter
 * @param {Function} predicate - The filter predicate
 * @param {number} maxResults - Maximum number of results to return
 * @returns {Array} - Filtered array with max results
 * 
 * @example
 * const results = optimizedFilter(
 *   largeArray,
 *   item => item.name.includes('search'),
 *   10
 * );
 */
export const optimizedFilter = (array, predicate, maxResults = Infinity) => {
  const results = [];
  
  for (let i = 0; i < array.length && results.length < maxResults; i++) {
    if (predicate(array[i])) {
      results.push(array[i]);
    }
  }
  
  return results;
};

/**
 * Lazy loading utility for dynamic imports
 * Useful for code splitting and reducing initial bundle size
 * 
 * @param {Function} importFunc - The dynamic import function
 * @param {Object} options - Options for lazy loading
 * @returns {Promise} - Promise that resolves to the loaded module
 * 
 * @example
 * const LazyComponent = lazyLoad(
 *   () => import('./HeavyComponent'),
 *   { timeout: 5000 }
 * );
 */
export const lazyLoad = (importFunc, options = {}) => {
  const { timeout = 10000, retries = 3 } = options;
  
  return new Promise((resolve, reject) => {
    let attempt = 0;
    
    const load = () => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`Lazy load timeout after ${timeout}ms`));
      }, timeout);
      
      importFunc()
        .then(module => {
          clearTimeout(timeoutId);
          resolve(module);
        })
        .catch(error => {
          clearTimeout(timeoutId);
          attempt++;
          
          if (attempt < retries) {
            setTimeout(load, 1000 * attempt); // Exponential backoff
          } else {
            reject(error);
          }
        });
    };
    
    load();
  });
};

export default {
  debounce,
  throttle,
  memoize,
  performanceMonitor,
  batch,
  createTTLCache,
  measureRenderTime,
  optimizedFilter,
  lazyLoad
}; 